import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { FileText, Search } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { InvoiceStatusBadge } from '@/components/features/invoicing/InvoiceStatusBadge'
import { useOrganizationInvoices } from '@/hooks/useOrganizationInvoices'
import { getProjectInvoicePdfUrl } from '@/lib/api/projects'
import { ApiError } from '@/lib/api/client'
import type { OrganizationInvoiceListItemDto } from '@/lib/api/v2/invoices'
import { formatCurrency, formatDate } from '@/lib/format'
import { getInvoiceTypeLabel, INVOICE_TYPES } from '@/lib/invoiceLabels'
import { cn } from '@/lib/utils'

type SortKey = 'issuedDate' | 'number' | 'amount'
type SortDir = 'asc' | 'desc'

const PAGE_SIZE = 25

function getTypeBadgeClassName(type: string) {
  if (type === 'credit_note') return 'rounded-md bg-rose-500/15 text-rose-700 border-0'
  if (type === 'deposit') return 'rounded-md bg-sky-500/15 text-sky-700 border-0'
  if (type === 'final') return 'rounded-md bg-emerald-500/15 text-emerald-700 border-0'
  return 'rounded-md bg-slate-500/10 text-slate-700 border-0'
}

/**
 * "Toutes les factures" tab of the invoices page: the org-wide ledger with
 * search, type/date filters, sorting and pagination.
 */
export function InvoicesLedgerTab() {
  const [page, setPage] = useState(1)
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({ key: 'issuedDate', dir: 'desc' })
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')

  // Debounced search: only hit the API 300ms after the user stops typing.
  // The page reset lives in the timeout callback (async, not a render-phase
  // updater); on mount both calls are value no-ops so React bails out.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput)
      setPage(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchInput])

  const { data, isLoading, error, refetch } = useOrganizationInvoices({
    page,
    pageSize: PAGE_SIZE,
    type: typeFilter === 'all' ? undefined : typeFilter,
    from: fromDate || undefined,
    to: toDate || undefined,
    q: search || undefined,
    orderBy: sort.key,
    orderDir: sort.dir,
  })

  const items = data?.items ?? []
  const totalCount = data?.totalCount ?? 0
  const totalAmountWithTax = data?.totalAmountWithTax ?? 0
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))
  const start = totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1
  const end = Math.min(page * PAGE_SIZE, totalCount)
  const hasActiveFilters = typeFilter !== 'all' || !!fromDate || !!toDate || !!search

  const toggleSort = (key: SortKey) => {
    setPage(1)
    setSort((prev) => (prev.key !== key ? { key, dir: 'desc' } : { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }))
  }

  const sortIndicator = (key: SortKey) => {
    if (sort.key !== key) return ''
    return sort.dir === 'asc' ? ' ↑' : ' ↓'
  }

  const handleOpenPdf = async (invoice: OrganizationInvoiceListItemDto) => {
    try {
      const { url } = await getProjectInvoicePdfUrl(invoice.projectId, invoice.id)
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch (e) {
      toast.error("Impossible d'ouvrir le PDF de la facture")
      console.error(e)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 sm:justify-end">
        <div className="text-xs text-muted-foreground" data-testid="invoices-page-range-label">
          Affichage {start}-{end} sur {totalCount}
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8"
            disabled={isLoading || page <= 1}
            onClick={() => setPage(page - 1)}
            data-testid="invoices-page-prev-btn"
          >
            Précédent
          </Button>
          <div className="text-xs text-muted-foreground">
            Page {page}/{totalPages}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8"
            disabled={isLoading || page >= totalPages}
            onClick={() => setPage(page + 1)}
            data-testid="invoices-page-next-btn"
          >
            Suivant
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="N° de facture ou client…"
            className="pl-8"
            data-testid="invoices-page-search-input"
          />
        </div>

        <div className="space-y-1">
          <span className="text-xs text-muted-foreground">Type</span>
          <Select
            value={typeFilter}
            onValueChange={(v) => {
              setTypeFilter(v)
              setPage(1)
            }}
          >
            <SelectTrigger className="w-[190px]" data-testid="invoices-page-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les types</SelectItem>
              {INVOICE_TYPES.map((t) => (
                <SelectItem key={t} value={t}>
                  {getInvoiceTypeLabel(t)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <span className="text-xs text-muted-foreground">Du</span>
          <Input
            type="date"
            value={fromDate}
            onChange={(e) => {
              setFromDate(e.target.value)
              setPage(1)
            }}
            className="w-[160px]"
            data-testid="invoices-page-from-input"
          />
        </div>

        <div className="space-y-1">
          <span className="text-xs text-muted-foreground">Au</span>
          <Input
            type="date"
            value={toDate}
            onChange={(e) => {
              setToDate(e.target.value)
              setPage(1)
            }}
            className="w-[160px]"
            data-testid="invoices-page-to-input"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
          <span className="text-xs text-muted-foreground">Trier par:</span>
          <Button
            type="button"
            variant={sort.key === 'issuedDate' ? 'default' : 'outline'}
            size="sm"
            className="h-7 text-xs"
            onClick={() => toggleSort('issuedDate')}
            data-testid="invoices-page-sort-date-btn"
          >
            Date{sortIndicator('issuedDate')}
          </Button>
          <Button
            type="button"
            variant={sort.key === 'number' ? 'default' : 'outline'}
            size="sm"
            className="h-7 text-xs"
            onClick={() => toggleSort('number')}
            data-testid="invoices-page-sort-number-btn"
          >
            Numéro{sortIndicator('number')}
          </Button>
          <Button
            type="button"
            variant={sort.key === 'amount' ? 'default' : 'outline'}
            size="sm"
            className="h-7 text-xs"
            onClick={() => toggleSort('amount')}
            data-testid="invoices-page-sort-amount-btn"
          >
            Montant{sortIndicator('amount')}
          </Button>
        </div>
      </div>

      {/* Summary band for the current selection */}
      <div
        className="flex flex-wrap items-center gap-x-6 gap-y-1 rounded-lg border bg-background px-4 py-3 text-sm"
        data-testid="invoices-page-summary"
      >
        <div>
          <span className="font-semibold">{totalCount}</span>{' '}
          <span className="text-muted-foreground">facture{totalCount > 1 ? 's' : ''}</span>
        </div>
        <div>
          <span className="text-muted-foreground">Total TTC (net) : </span>
          <span className="font-semibold tabular-nums">{formatCurrency(totalAmountWithTax)}</span>
        </div>
        {hasActiveFilters && (
          <span className="text-xs text-muted-foreground">sur la sélection filtrée</span>
        )}
      </div>

      {/* List */}
      <div className="relative">
        {isLoading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-background/60 backdrop-blur-[1px]">
            <div className="rounded-md border bg-background px-3 py-2 text-xs text-muted-foreground shadow-sm">
              Chargement…
            </div>
          </div>
        )}

        {error != null ? (
          <div className="rounded-lg border bg-background py-10 text-center text-sm">
            {error instanceof ApiError && error.status === 403 ? (
              <p className="text-muted-foreground">
                Vous n'avez pas la permission de consulter les factures.
              </p>
            ) : (
              <>
                <p className="text-destructive">Impossible de charger les factures.</p>
                <Button type="button" variant="outline" size="sm" className="mt-3" onClick={refetch} data-testid="invoices-page-retry-btn">
                  Réessayer
                </Button>
              </>
            )}
          </div>
        ) : items.length === 0 && !isLoading ? (
          <div className="rounded-lg border bg-background py-10 text-center text-sm text-muted-foreground" data-testid="invoices-page-empty">
            {hasActiveFilters ? 'Aucune facture ne correspond aux filtres.' : 'Aucune facture générée pour le moment.'}
          </div>
        ) : (
          <div className="divide-y rounded-lg border bg-background">
            {items.map((invoice) => (
              <div
                key={invoice.id}
                className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:justify-between"
                data-testid="invoices-page-row"
              >
                <div className="min-w-0 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{invoice.number}</span>
                    <Badge className={getTypeBadgeClassName(invoice.type)}>
                      {getInvoiceTypeLabel(invoice.type)}
                    </Badge>
                    <InvoiceStatusBadge
                      isSuperseded={invoice.isSuperseded}
                      supersededByInvoiceId={invoice.supersededByInvoiceId}
                      supersededAt={invoice.supersededAt}
                    />
                  </div>
                  <div className="flex flex-wrap items-center gap-x-2 text-sm text-muted-foreground">
                    {invoice.clientName ? (
                      <Link to={`/clients/${invoice.customerId}`} className="hover:underline">
                        {invoice.clientName}
                      </Link>
                    ) : (
                      <span>—</span>
                    )}
                    <span>·</span>
                    <Link to={`/projects/${invoice.projectId}?tab=invoices`} className="hover:underline">
                      {invoice.projectReference || 'Projet'}
                      {invoice.projectName ? ` — ${invoice.projectName}` : ''}
                    </Link>
                    {invoice.vatPercents && invoice.vatPercents.length > 0 && (
                      <>
                        <span>·</span>
                        <span>TVA {invoice.vatPercents.join(' / ')}%</span>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-4 sm:gap-6">
                  <div className="text-sm text-muted-foreground">{formatDate(invoice.invoiceDate)}</div>
                  <div
                    className={cn(
                      'w-28 text-right font-semibold tabular-nums',
                      invoice.type === 'credit_note' && 'text-red-600',
                    )}
                  >
                    {formatCurrency(invoice.amountWithTax)}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!invoice.hasPdf}
                    title={invoice.hasPdf ? 'Ouvrir le PDF' : 'PDF non disponible'}
                    onClick={() => handleOpenPdf(invoice)}
                    data-testid="invoices-page-row-pdf-btn"
                  >
                    <FileText className="mr-1 h-4 w-4" />
                    PDF
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
